import { Link } from 'expo-router';
import Head from 'expo-router/head';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { CityCardImage } from '@/components/riviera/city-card-image';
import { Faq, landingStyles } from '@/components/riviera/landing-parts';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Fonts, MaxContentWidth, Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { cities, type City } from '@/lib/events-data';
import { citiesHubCopy } from '@/lib/i18n/cities-hub-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import {
  absoluteUrl,
  breadcrumbJsonLd,
  faqPageJsonLd,
  hreflangLinks,
  itemListJsonLd,
} from '@todo/core/seo';

// The cities hub — the destination for the site header's "Cities" nav item
// (previously a dead placeholder, see nav-links.ts), and the crawlable index
// into the 7 city landing pages. Pure static content: `cities` is a fixed
// constant, no data fetch needed, unlike the events/tours/articles hubs.
// Hand-written intro, per-card taglines and FAQ (`citiesHub` in the i18n
// resources) compare the towns with each other — deliberately not a copy of
// the city pages' own `cityPages.<city>.intro`, which those pages rank with.

// The first row of cards on a desktop-width page (MaxContentWidth / 220px
// cards) loads eagerly; the rest are lazy.
const EAGER_CARDS = 5;

function CityCard({
  city,
  locale,
  tagline,
  eager,
}: {
  city: City;
  locale: string;
  tagline: string | undefined;
  eager: boolean;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Link href={{ pathname: '/[lang]/[city]', params: { lang: locale, city: city.id } }} asChild>
      <Pressable style={({ pressed }) => [styles.cardPressable, pressed && { opacity: 0.85 }]}>
        <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <View style={styles.image}>
            <CityCardImage
              image={city.cardImage}
              imageSm={city.cardImageSm}
              alt={t('citiesHub.imageAlt', { city: city.name })}
              eager={eager}
            />
          </View>
          <View style={styles.body}>
            <Text style={[styles.title, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {city.name}
            </Text>
            {tagline ? (
              <Text style={[styles.tagline, { color: theme.textSecondary }]}>{tagline}</Text>
            ) : null}
          </View>
        </View>
      </Pressable>
    </Link>
  );
}

export default function CitiesIndexScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();

  const copy = citiesHubCopy(locale);
  const canonical = absoluteUrl(locale, 'cities');

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{t('seo.citiesTitle')}</title>
        <meta name="description" content={t('seo.citiesDescription')} />
        <link rel="canonical" href={canonical} />
        {hreflangLinks('cities').map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('seo.citiesTitle')} />
        <meta property="og:description" content={t('seo.citiesDescription')} />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:title" content={t('seo.citiesTitle')} />
        <meta name="twitter:description" content={t('seo.citiesDescription')} />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [{ name: t('nav.cities') }]))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(
            itemListJsonLd(
              cities.map((city) => ({ name: city.name, url: absoluteUrl(locale, city.id) })),
              canonical,
            ),
          )}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(copy.faq))}</script>
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView
        style={{ flex: 1 }}>
        <View style={styles.centered}>
          <View style={styles.page}>
            {/* role="heading" + aria-level renders a real <h1> on web (via
                react-native-web) while staying valid on native. */}
            <Text
              role="heading"
              aria-level={1}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {t('citiesHub.heading')}
            </Text>
            {copy.intro.map((paragraph) => (
              <Text key={paragraph} style={[landingStyles.intro, { color: theme.textSecondary }]}>
                {paragraph}
              </Text>
            ))}
            <View style={styles.grid}>
              {cities.map((city, i) => (
                <CityCard
                  key={city.id}
                  city={city}
                  locale={locale}
                  tagline={copy.taglines[city.id]}
                  eager={i < EAGER_CARDS}
                />
              ))}
            </View>
            <Faq heading={copy.faqHeading} entries={copy.faq} />
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    gap: Spacing.three,
  },
  heading: {
    fontSize: 26,
    fontWeight: '700',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.three,
    justifyContent: 'center',
  },
  cardPressable: {
    width: 220,
    flexGrow: 0,
    flexShrink: 0,
  },
  card: {
    // Fills the row's height, so cards with a shorter tagline line up.
    flexGrow: 1,
    width: 220,
    borderRadius: Radius.xl,
    borderWidth: 1,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: 140,
  },
  body: {
    padding: Spacing.three - 2,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
  },
  tagline: {
    fontSize: 13,
    lineHeight: 19,
    marginTop: 4,
  },
});
